import { useEffect, useRef, useState } from 'preact/hooks';
import { effect, batch } from '@preact/signals';
import { Viewer, WebGLUnavailableError, type AssetManifest, type Side } from '../viewer/Viewer';
import { LAYERS } from '../viewer/layers';
import * as st from '../state/store';
import { BY_ID, resolveId } from '../state/structures';
import { fromUrl, toUrl } from '../state/url';
import { TopBar } from './TopBar';
import { LayerPanel } from './LayerPanel';
import { tier, initEntitlement } from '../entitlements/tier';
import { UnlockDialog } from './UnlockDialog';
import { exportImage } from '../export';
import { CardPanel } from './CardPanel';
import { ExportDialog } from './ExportDialog';
import { OfflinePanel } from './OfflinePanel';
import { OfflineBanner } from './OfflineBanner';
import { UpdatePrompt } from './UpdatePrompt';
import { LaserPointer, pointAt } from './LaserPointer';
import { HoverLabel } from './HoverLabel';
import { Toast } from './Toast';
import { FirstRunHint } from './FirstRunHint';
import { HelpOverlay } from './HelpOverlay';
import { Fallback } from './Fallback';
import { LoadingBar } from './LoadingBar';
import { viewerRef } from './viewerRef';
import { track, memory as analyticsMemory, entryOf, startAnalytics } from '../analytics';
import { TOUR_BY_ID, applyStop, gotoStop, loadStops, startTour } from '../tours/tours';

/** Flies to a selection and fades layers that hide it. */
function focusSelection(id: string, side?: Side, reveal = true) {
  const v = viewerRef.current;
  if (!v) return;
  // Parts of the female body load on first use (F22): frame and reveal once they're in.
  void v.bodyReady().then(() => frameAndReveal(v, id, side, reveal));
}

function frameAndReveal(v: Viewer, id: string, side: Side | undefined, reveal: boolean) {
  const s = BY_ID.get(id);
  const preset = s?.bestView?.preset;
  const cam = v.frame(id, { side, preset: preset === 'front' || preset === 'back' || preset === 'left' || preset === 'right' || preset === 'top' ? preset : undefined, zoom: s?.bestView?.zoom });
  if (reveal && cam) {
    // The selection itself stays solid even when its own layer is faded (Viewer.modeOf).
    const block = v.occludingLayers(id, cam, side).filter((l) => l !== 'skin');
    if (block.length) st.ghostLayers(block);
  }
}

const prefersReducedMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

export function App() {
  // Magic link from the receipt email (F13): /unlock#key=… The key stays in the fragment, so it never reaches a server.
  const [magicKey] = useState(() => {
    if (location.pathname.replace(/\/$/, '') !== '/unlock') return undefined;
    const key = new URLSearchParams(location.hash.slice(1)).get('key') ?? '';
    history.replaceState(null, '', '/');
    st.panel.value = 'unlock';
    return key || undefined;
  });
  useEffect(() => void initEntitlement().catch(() => undefined), []);
  // Analytics (F10): the landing event is recorded now; the provider sink loads after first paint.
  useEffect(() => {
    track('landing', { entry: entryOf(location.pathname, location.search) });
    const start = () => void startAnalytics().catch(() => undefined);
    if ('requestIdleCallback' in window) requestIdleCallback(start, { timeout: 4000 });
    else setTimeout(start, 2000);
  }, []);
  const host = useRef<HTMLDivElement>(null);
  const [failed, setFailed] = useState<null | 'webgl' | 'load'>(null);
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null);
  const [help, setHelp] = useState(false);
  const helpOpen = useRef(false);
  helpOpen.current = help;

  // Boot: viewer + initial state from URL.
  useEffect(() => {
    const initial = fromUrl(location.pathname, location.search, (id) => !!resolveId(id));
    if (st.classroomRequested()) st.toggleClassroom(true);
    let sel = initial.selected ? { ...initial.selected, id: resolveId(initial.selected.id)! } : null;
    if (sel && st.sensitiveHidden() && BY_ID.get(sel.id)?.sensitive) {
      sel = null;
      st.showToast('That part is hidden in classroom mode. The teacher can show it from the menu.');
    }
    // Body (F22): a part that exists in one body decides it; else the link's `b`; else the remembered preference.
    st.setBody((sel && BY_ID.get(sel.id)?.body) || initial.body || st.preferredBody(), { remember: false });
    batch(() => {
      st.layers.value = initial.layers;
      st.hidden.value = initial.hidden;
      st.isolate.value = initial.isolate;
      if (sel) st.select(sel.id, sel.side);
    });
    if (initial.unknownSlug) st.showToast("We couldn't find that part. Try searching for it.");
    if (initial.tour) {
      if (TOUR_BY_ID.has(initial.tour.id)) startTour(initial.tour.id, initial.tour.stop);
      else st.showToast("We couldn't find that tour.");
    }

    let viewer: Viewer;
    let disposed = false;
    (async () => {
      try {
        const manifest = (await (await fetch('/assets/assets.json')).json()) as AssetManifest;
        if (disposed) return;
        viewer = new Viewer(host.current!, manifest, {
          onPick: (p) => {
            if (p) {
              st.revealOnSelect.value = false; // a clicked part is already visible
              st.select(p.id, p.side);
              queueMicrotask(() => (st.revealOnSelect.value = true));
              track('card_open', { structure_id: p.id, source: 'click' });
            } else st.select(null);
          },
          onHover: (p, x, y) => setHover(p ? { id: p.id, x, y } : null),
          onCameraChange: (c) => (st.camera.value = c),
          onLayerLoaded: (layer, done, total) => {
            st.loadedLayers.value = new Set([...st.loadedLayers.value, layer]);
            st.loadProgress.value = done / total;
          },
          onFirstRender: () => document.documentElement.setAttribute('data-first-render', String(Math.round(performance.now()))),
        });
        viewer.reducedMotion = prefersReducedMotion();
        viewer.init();
        viewerRef.current = viewer;
        (window as unknown as { __marrow: unknown }).__marrow = { viewer, store: st, analytics: analyticsMemory, tier, exportImage };
        if (initial.camera) viewer.setCamera(initial.camera, false);
        await viewer.loadDefault();
        document.documentElement.setAttribute('data-loaded', String(Math.round(performance.now())));
        viewer.warmUp();
        if (sel && !initial.camera) focusSelection(sel.id, sel.side);
        else if (sel) void viewer.loadRegion(BY_ID.get(sel.id)?.region ?? '');
      } catch (e) {
        console.warn(e);
        setFailed(e instanceof WebGLUnavailableError ? 'webgl' : 'load');
      }
    })();
    return () => {
      disposed = true;
      viewer?.dispose();
    };
  }, []);

  // State → viewer.
  useEffect(
    () =>
      effect(() => {
        const v = viewerRef.current;
        const layers = st.layers.value;
        const sel = st.selected.value;
        const hidden = new Set(st.hidden.value);
        const iso = st.isolate.value;
        const sensitive = st.classroom.value && !st.showSensitive.value ? new Set([...BY_ID.values()].filter((s) => s.sensitive).map((s) => s.id)) : undefined;
        if (sensitive && st.selected.value && sensitive.has(st.selected.value.id)) queueMicrotask(() => st.select(null));
        void st.loadedLayers.value; // re-apply when layers arrive
        const body = st.body.value;
        if (!v) return;
        if (v.getBody() !== body) void v.setBody(body);
        v.setLayers(layers);
        v.setVisibility({ hidden, isolate: iso, hideSensitive: sensitive });
        v.select(sel?.id ?? null, sel?.side);
        v.setHighlight(st.highlight.value);
      }),
    [],
  );

  // Tour stops (F11): apply each stop's view when the tour or stop changes.
  useEffect(() => {
    let lastKey = '';
    return effect(() => {
      const t = st.tour.value;
      void st.loadedLayers.value;
      const key = t ? `${t.id}:${t.stop}` : '';
      if (!t || key === lastKey || !viewerRef.current) return;
      lastKey = key;
      void loadStops(t.id).then((stops) => {
        const stop = stops?.[t.stop];
        const v = viewerRef.current;
        if (!stop || !v || st.tour.value?.id !== t.id || st.tour.value.stop !== t.stop) return;
        const cam = applyStop(stop);
        if (cam) v.setCamera(cam);
        else v.frame(stop.focus);
        track('tour_stop', { tour_id: t.id, stop: t.stop });
      });
    });
  }, []);

  // Fly to new selections.
  useEffect(() => {
    let prev: string | null = st.selected.value?.id ?? null;
    return effect(() => {
      const sel = st.selected.value;
      const id = sel?.id ?? null;
      if (id && id !== prev) {
        // Let the card render first; camera flight and occlusion checks follow on the next task.
        const reveal = st.revealOnSelect.value;
        setTimeout(() => focusSelection(id, sel?.side, reveal), 0);
      }
      prev = id;
    });
  }, []);

  // State → URL. Selection changes push history; camera/layer tweaks replace it.
  useEffect(() => {
    let lastSel = st.selected.value?.id ?? null;
    let timer = 0;
    const off = effect(() => {
      const url = toUrl({ selected: st.selected.value, camera: st.camera.value, layers: st.layers.value, hidden: st.hidden.value, isolate: st.isolate.value, tour: st.tour.value, body: st.body.value });
      const selId = st.selected.value?.id ?? (st.tour.value ? `tour:${st.tour.value.id}:${st.tour.value.stop}` : null);
      clearTimeout(timer);
      if (selId !== lastSel) {
        lastSel = selId;
        history.pushState(null, '', url);
      } else timer = window.setTimeout(() => history.replaceState(null, '', url), 250);
    });
    const onPop = () => {
      const s = fromUrl(location.pathname, location.search, (id) => !!resolveId(id));
      lastSel = s.selected?.id ?? null;
      if (s.body) st.setBody(s.body, { remember: false });
      batch(() => {
        st.layers.value = s.layers;
        st.hidden.value = s.hidden;
        st.isolate.value = s.isolate;
        if (s.selected) st.select(resolveId(s.selected.id)!, s.selected.side);
        else st.select(null);
      });
      if (s.camera) viewerRef.current?.setCamera(s.camera);
    };
    addEventListener('popstate', onPop);
    return () => {
      off();
      removeEventListener('popstate', onPop);
    };
  }, []);

  // Keyboard shortcuts (F01/F03).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t.closest('input, textarea, select, [contenteditable]')) return;
      const v = viewerRef.current;
      const k = e.key;
      if ((e.ctrlKey || e.metaKey) && k.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) st.redo();
        else st.undo();
        return;
      }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      // Presentation clickers send PageDown/PageUp.
      if (st.tour.value && !st.selected.value && (k === 'ArrowLeft' || k === 'ArrowRight' || k === ' ' || k === 'PageDown' || k === 'PageUp')) {
        e.preventDefault();
        gotoStop(st.tour.value.stop + (k === 'ArrowLeft' || k === 'PageUp' ? -1 : 1));
        return;
      }
      const step = Math.PI / 18;
      const map: Record<string, () => void> = {
        ArrowLeft: () => v?.orbitBy(-step, 0),
        ArrowRight: () => v?.orbitBy(step, 0),
        ArrowUp: () => v?.orbitBy(0, -step),
        ArrowDown: () => v?.orbitBy(0, step),
        '+': () => v?.zoomBy(0.85),
        '=': () => v?.zoomBy(0.85),
        '-': () => v?.zoomBy(1.18),
        r: () => v?.reset(),
        f: () => st.selected.value && v?.frame(st.selected.value.id, { side: st.selected.value.side }),
        i: () => st.isolateSelected('alone'),
        h: () => st.hideSelected(),
        Escape: () => (st.panel.value === 'unlock' ? (st.panel.value = null) : st.exportOpen.value ? (st.exportOpen.value = false) : st.offlineOpen.value ? (st.offlineOpen.value = false) : helpOpen.current ? setHelp(false) : st.hidden.value.length || st.isolate.value ? st.showAll() : st.select(null)),
        '?': () => setHelp((x) => !x),
        p: () => st.peel(),
      };
      const fn = map[k] ?? map[k.toLowerCase()];
      if (fn) {
        if (k.startsWith('Arrow')) e.preventDefault();
        fn();
      }
    };
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, []);

  // Panels overlay the canvas; keep the body centred in the uncovered area.
  useEffect(
    () =>
      effect(() => {
        const open = st.panel.value === 'card' && !!st.selected.value;
        const sheetState = st.sheet.value;
        const v = viewerRef.current;
        if (!v) return;
        const w = innerWidth;
        if (w >= 600) v.setInsets({ right: open ? (w >= 1024 ? (st.classroom.value ? 572 : 412) : 372) : 0 });
        else v.setInsets({ bottom: open ? (sheetState === 'peek' ? 120 : sheetState === 'full' ? innerHeight - 64 : innerHeight * 0.45) : 0 });
      }),
    [],
  );

  if (failed) return <Fallback reason={failed} />;
  return (
    <div class={`app ${st.classroom.value ? 'classroom' : ''} ${st.selected.value || st.panel.value === 'tours' || st.tour.value ? 'card-open' : ''}`} data-testid="app-root">
      <TopBar onHelp={() => setHelp(true)} />
      <div class="stage" ref={host} data-testid="stage" onPointerDown={(e) => st.classroom.value && e.isPrimary && pointAt(e.clientX, e.clientY)}>
        <LoadingBar />
      </div>
      <LayerPanel />
      <CardPanel />
      {st.exportOpen.value && <ExportDialog />}
      {st.offlineOpen.value && <OfflinePanel />}
      {st.panel.value === 'unlock' && <UnlockDialog initialKey={magicKey} />}
      <OfflineBanner />
      <UpdatePrompt />
      {hover && <HoverLabel {...hover} />}
      <FirstRunHint />
      <Toast />
      <LaserPointer />
      {help && <HelpOverlay onClose={() => setHelp(false)} />}
      <span class="visually-hidden" aria-live="polite" data-testid="live">
        {st.selected.value ? `Selected: ${BY_ID.get(st.selected.value.id)?.title ?? ''}, ${LAYERS.find((l) => l.id === BY_ID.get(st.selected.value!.id)?.layer)?.label ?? ''}` : ''}
      </span>
    </div>
  );
}
